import { Container, Paper, Stack, Title, Text, Group, ActionIcon, Button, Popover, TextInput, Center, Loader, Table, Badge, Switch } from '@mantine/core';
import { useForm, isEmail, isNotEmpty, UseFormReturnType } from '@mantine/form';
import { IconTrash } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createToast } from './Toast';
import { MessageKind, refreshPage } from '../utils/common';
import { handleApiError } from '../utils/app';
import API from '../utils/const';
import { authFetch } from '../utils/session';
import { format_total, PaddleSubscription, parseTotal, RecurringMemberDetails } from '../utils/paddle';
import { User } from '../utils/session';
import { Team } from '../utils/team';

interface BasePopoverProps {
    buttonText: string;
    buttonVariant?: 'filled' | 'outline';
    form: UseFormReturnType<any, (values: any) => any>;
    onSubmit: (values: any) => Promise<any>;
    children: React.ReactNode;
    width?: number;
    showCancel?: boolean;
    onClickValidate?: () => boolean;
}

const BasePopover: React.FC<BasePopoverProps> = ({
    buttonText,
    buttonVariant = 'outline',
    form,
    onSubmit,
    children,
    width = 300,
    onClickValidate,
}) => {
    const { t } = useTranslation(["app"]);
    const [opened, setOpened] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const handleSubmit = async (values: any) => {
        setOpened(false);
        setIsLoading(true);
        await onSubmit(values).finally(() => setIsLoading(false));
    }


    const onPopoverClick = () => {
        if (!onClickValidate) {
            setOpened(true)
            return
        }
        if (onClickValidate()) {
            setOpened(true)
        } else {
            setOpened(false)
        }
    }
    return (
        <Popover position="bottom" withArrow shadow="md" opened={opened} onClose={() => setOpened(false)} onChange={setOpened}>
            <Popover.Target>
                <Button variant={buttonVariant} onClick={onPopoverClick}>{buttonText}</Button>
            </Popover.Target>
            <Popover.Dropdown miw={width}>
                <form onSubmit={form.onSubmit((values) => handleSubmit(values))}>
                    <Stack gap='sm'>
                        {children}
                        <Group gap="sm">
                            <Button type='submit' loading={isLoading}>{t("common.submit")}</Button>
                            <Button variant='outline' onClick={() => setOpened(false)}>{t("common.cancel")}</Button>
                        </Group>
                    </Stack>
                </form>
            </Popover.Dropdown>
        </Popover>
    );
}


const CreateTeamPopover: React.FC<{ callback: (values: { name: string, contact_email: string }) => Promise<any>, onClickValidate: () => boolean }> = ({ callback, onClickValidate }) => {
    const { t } = useTranslation(["app"]);
    const form = useForm({
        initialValues: { name: "", contact_email: "" },
        validate: {
            contact_email: (value) => isEmail(value) ? null : "Invalid email",
            name: (value) => isNotEmpty(value) ? null : "Name is required"
        }
    });

    return (
        <BasePopover
            buttonText={t("dashboard.create-team")}
            buttonVariant="filled"
            form={form}
            onSubmit={(values) => callback({ name: values.name, contact_email: values.contact_email })}
            onClickValidate={onClickValidate}
        >
            <TextInput placeholder={t("input.team-name")} {...form.getInputProps('name')} />
            <TextInput type='email' placeholder={t("input.contact-email")} {...form.getInputProps('contact_email')} />
        </BasePopover>
    );
}

const JoinTeamPopover: React.FC<{ callback: (values: { email: string, uuid: string }) => Promise<any> }> = ({ callback }) => {
    const { t } = useTranslation(["app"]);
    const form = useForm({
        initialValues: { email: "", uuid: "" },
        validate: {
            email: (value) => isEmail(value) ? null : "Invalid email",
            uuid: (value) => isNotEmpty(value) ? null : "UUID is required"
        }
    });

    return (
        <BasePopover
            buttonText={t("dashboard.team.join")}
            form={form}
            onSubmit={(values) => callback({ email: values.email, uuid: values.uuid })}
        >
            <TextInput placeholder={t("input.email")} {...form.getInputProps('email')} />
            <TextInput placeholder={t("input.uuid")} {...form.getInputProps('uuid')} />
        </BasePopover>
    );
}

const AddMemberPopover: React.FC<{ callback: (values: { email: string }) => Promise<any> }> = ({ callback }) => {
    const { t } = useTranslation(["app"]);
    const form = useForm({
        initialValues: { email: "" },
        validate: {
            email: (value) => isEmail(value) ? null : "Invalid email",
        }
    });

    return (
        <BasePopover
            buttonText={t("dashboard.team.add-member")}
            form={form}
            onSubmit={(values) => callback({ email: values.email })}
        >
            <TextInput placeholder={t("input.email")} {...form.getInputProps('email')} />
        </BasePopover>
    );
}

const RemoveMemberPopover: React.FC<{ callback: () => Promise<any> }> = ({ callback }) => {
    const { t } = useTranslation(["app"]);
    const [opened, setOpened] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const handleRemove = () => {
        setOpened(false);
        setIsLoading(true);
        callback().finally(() => setIsLoading(false));
    }

    return (
        <Popover width={300} trapFocus position="bottom" withArrow shadow="md" opened={opened} onClose={() => setOpened(false)} onChange={setOpened}>
            <Popover.Target>
                <ActionIcon variant='subtle' onClick={() => setOpened(true)}><IconTrash /></ActionIcon>
            </Popover.Target>
            <Popover.Dropdown>
                <Stack gap='sm'>
                    <Text fw={300} size='sm'>{t("dashboard.team.remove-confirm")}</Text>
                    <Group>
                        <Button variant='filled' loading={isLoading} color='red' onClick={handleRemove}>{t("common.yes")}</Button>
                        <Button variant='outline' onClick={() => { setOpened(false) }}>Cancel</Button>
                    </Group>
                </Stack>
            </Popover.Dropdown>
        </Popover>)
}


export const TeamComponent: React.FC<{ team: Team | null, setTeam: (team: Team | null) => void, user: User | null, subscription: PaddleSubscription | null }> = ({ team, setTeam, user, subscription }) => {
    const { t } = useTranslation(['app']);
    const [isLoading, setIsLoading] = useState(true);
    const [recurringMemberDetails, setRecurringMemberDetails] = useState<RecurringMemberDetails | null>(null);

    useEffect(() => {
        if (team != null && team.is_owner && subscription?.recurring_transaction_details && subscription.next_transaction != null) {
            setRecurringMemberDetails({
                price: parseTotal(subscription.recurring_transaction_details.line_items[0].unit_totals.subtotal) +
                    parseTotal(subscription.recurring_transaction_details.line_items[0].unit_totals.tax),
                tax: parseTotal(subscription.recurring_transaction_details.line_items[0].unit_totals.tax),
                currency: subscription.next_transaction.details.totals.currency_code
            });
        }
    }, [team, subscription]);

    const createTeam = async (name: string, contact_email: string) => {
        if (user == null) {
            createToast("You must be logged in to create a team", MessageKind.Error);
            return
        }
        else if (team != null) {
            createToast("You are already part of a team", MessageKind.Error);
            return
        }
        else if (subscription == null) {
            createToast("You must have an active subscription to create a team", MessageKind.Error);
            return
        }

        const response = await authFetch(`${API}/team`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ name, contact_email })
        });
        if (!response.ok) {
            createToast("Error occured while fetching your team. If this continues, please contact support.", MessageKind.Error);
            return;
        }
        let response_team: Team = await response.json();
        setTeam(response_team)
    }


    const joinTeam = async (invite_email: string, uuid: string) => {
        const response = await authFetch(`${API}/team/join`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ email: invite_email, uuid })
        });
        if (!response.ok) {
            handleApiError(response, t, true)
            return;
        }
        refreshPage()
    }

    const addMemberToTeam = async (email: string) => {
        const response = await authFetch(`${API}/team/add`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ email })
        });
        if (!response.ok) {
            handleApiError(response, t, true)
            return;
        }
        let response_team: Team = await response.json();
        setTeam(response_team)
    }


    const getTeam = async () => {
        const response = await authFetch(`${API}/team`, {
            method: 'GET',
        });
        if (!response.ok) {
            createToast("Error occured while fetching your team. If this continues, please contact support.", MessageKind.Error);
            return;
        }
        let response_team: Team = await response.json();
        return response_team
    }

    const removeMemberFromTeam = async (email: string) => {
        const response = await authFetch(`${API}/team/remove`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ email })
        });
        if (!response.ok) {
            handleApiError(response, t, true)
            return;
        }
        let response_team: Team = await response.json();
        setTeam(response_team)
    }

    const setTeamManager = async (email: string, can_manage: boolean) => {
        const response = await fetch(`${API}/team/managers`, {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ email, can_manage })
        });
        if (!response.ok) {
            handleApiError(response, t, true)
            return;
        }
        let response_team: Team = await response.json();
        setTeam(response_team)
    }


    const onClickValidate = () => {
        if (subscription == null) {
            createToast("You must have an active subscription to create a team", MessageKind.Error);
            return false
        }
        if (team != null) {
            createToast("You are already part of a team", MessageKind.Error);
            return false
        }
        return true
    }

    useEffect(() => {
        if (user == null) {
            setIsLoading(true)
            return
        }
        setIsLoading(false)
        if (user.has_team) {
            setIsLoading(true)
            getTeam().then((team) => {
                setTeam(team)
            }).finally(() => setIsLoading(false))
        }
    }, [user])

    return (
        <>
            <Container p={4} mt='lg' size='lg'>
                <Paper withBorder p='md'>

                    {isLoading &&
                        <Center>
                            <Loader />
                        </Center>
                    }
                    {team == null &&
                        <>
                            <Stack gap='md' align="flex-start">
                                <div>
                                    <Title order={3} fw={400}>{t("dashboard.team.your-team")}</Title>
                                    <Text size='sm' c='dimmed'>
                                        {t("dashboard.team.description")}
                                    </Text>
                                </div>
                                <Group>
                                    <CreateTeamPopover onClickValidate={onClickValidate} callback={({ name, contact_email }) => createTeam(name, contact_email)} />
                                    <JoinTeamPopover callback={({ email, uuid }) => joinTeam(email, uuid)} />
                                </Group>
                            </Stack>
                        </>
                    }
                    {team != null &&
                        <Stack gap='lg'>
                            <Group justify='space-between' align='center'>
                                {(team.is_owner || team.can_manage) &&
                                    <>
                                        <Title order={3} fw={400}>{team.is_owner ? t("dashboard.team.your-team") : t("dashboard.team.managing-team", { name: team.name })}</Title>
                                        <AddMemberPopover callback={({ email }) => addMemberToTeam(email)} />
                                    </>
                                }
                                {!team.is_owner && !team.can_manage &&
                                    <Title order={3} fw={400}>{t("dashboard.team.part-of-team", { name: team.name })}</Title>
                                }
                            </Group>
                            {team.is_owner &&
                                <Stack gap={0}>
                                    <Text c='dimmed' size='sm'>{t("dashboard.team.note")}</Text>
                                    {recurringMemberDetails != null &&
                                        <Text c='dimmed' size='sm'>{t("dashboard.team.billing-info", { price: format_total(recurringMemberDetails.price, recurringMemberDetails.currency) })}</Text>
                                    }
                                </Stack>
                            }

                            {team.can_manage &&
                                <Table striped>
                                    <Table.Thead>
                                        <Table.Tr>
                                            <Table.Th>{t("dashboard.team.user")}</Table.Th>
                                            <Table.Th>{t("dashboard.team.status")}</Table.Th>
                                            {team.is_owner && <Table.Th>{t("dashboard.team.manager")}</Table.Th>}
                                            <Table.Th>{t("dashboard.team.actions")}</Table.Th>
                                        </Table.Tr>
                                    </Table.Thead>
                                    <Table.Tbody>
                                        {team.users.map((member) => (
                                            <Table.Tr key={member.email}>
                                                <Table.Td>{member.email}</Table.Td>
                                                <Table.Td>
                                                    {member.user_uuid &&
                                                        <>
                                                            <Badge color='blue' radius="md">{t("dashboard.team.active")}</Badge>
                                                            {(member.can_manage || member.email === team.contact_email) &&
                                                                <Badge color='violet' radius="md" ml='xs'>{t("dashboard.team.manager-badge")}</Badge>
                                                            }
                                                        </>
                                                    }
                                                    {!member.user_uuid &&
                                                        <Badge color='gray' radius="md">{t("dashboard.team.invited")}</Badge>
                                                    }
                                                </Table.Td>
                                                {team.is_owner &&
                                                    <Table.Td style={{ verticalAlign: 'middle' }}>
                                                        {member.email !== team.contact_email && member.user_uuid &&
                                                            <Switch
                                                                checked={member.can_manage ?? false}
                                                                onChange={(event) => setTeamManager(member.email, event.currentTarget.checked)}
                                                                aria-label={t("dashboard.team.manager-toggle", { email: member.email })}
                                                            />
                                                        }
                                                    </Table.Td>
                                                }
                                                <Table.Td style={{ verticalAlign: 'middle' }}>
                                                    {member.email !== team.contact_email && !(member.can_manage && !team.is_owner) &&
                                                        <RemoveMemberPopover callback={() => removeMemberFromTeam(member.email)} />
                                                    }
                                                </Table.Td>
                                            </Table.Tr>
                                        ))}
                                    </Table.Tbody>
                                </Table>
                            }
                            {!team.is_owner && !team.can_manage &&
                                <p>{t("dashboard.team.owner", { email: team.contact_email })}</p>
                            }
                        </Stack>
                    }
                </Paper>
            </Container>
        </>
    )
}